// src/pages/auth/ResetPasswordPage.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { 
  Card, 
  Form, 
  Button, 
  Alert, 
  Spinner,
  InputGroup,
  Container,
  Row,
  Col
} from "react-bootstrap";
import { 
  Lock, 
  CheckCircle, 
  XCircle, 
  Eye, 
  EyeSlash,
  ArrowLeft,
  Shield,
  Key
} from "react-bootstrap-icons";

const API_BASE_URL = process.env.REACT_APP_API_URL;

const ResetPasswordPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [tokenValid, setTokenValid] = useState(true);
  const [checkingToken, setCheckingToken] = useState(true);
  
  // Validation des mots de passe
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });
  
  // Vérifier la validité du token au chargement
  useEffect(() => {
    const verifyToken = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/bo/verify-reset-token/${token}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });
        
        const data = await response.json();
        
        if (response.ok && data.valid === true) {
          setTokenValid(true);
        } else {
          setTokenValid(false);
          setError(data.message || "Le lien de réinitialisation est invalide ou a expiré");
        }
      } catch (err) {
        console.error("Erreur lors de la vérification du token:", err);
        setTokenValid(false);
        setError("Erreur de connexion. Veuillez réessayer.");
      } finally {
        setCheckingToken(false);
      }
    };
    
    if (token) {
      verifyToken();
    } else {
      setTokenValid(false);
      setError("Token de réinitialisation manquant");
      setCheckingToken(false);
    }
  }, [token]);
  
  // Vérifier la force du mot de passe
  useEffect(() => {
    setPasswordStrength({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    });
  }, [password]);
  
  const getPasswordStrengthScore = () => {
    const strengths = Object.values(passwordStrength);
    const score = strengths.filter(Boolean).length;
    if (score <= 2) return { text: "Faible", color: "danger", progress: 25 };
    if (score <= 3) return { text: "Moyen", color: "warning", progress: 50 };
    if (score <= 4) return { text: "Fort", color: "info", progress: 75 };
    return { text: "Très fort", color: "success", progress: 100 };
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!password) {
      setError("Veuillez entrer un mot de passe");
      return;
    }
    
    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      return;
    }
    
    if (password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères");
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/bo/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          token: token,
          password: password,
          password_confirmation: confirmPassword
        })
      });
      
      const data = await response.json();
      
      if (response.ok && (data.success === true || data.code === 200)) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(data.message || "Erreur lors de la réinitialisation du mot de passe");
      }
    } catch (err) {
      console.error("Erreur:", err);
      setError("Erreur de connexion. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };
  
  // Écran de chargement
  if (checkingToken) {
    return (
      <div className="reset-password-page">
        <Container fluid className="vh-100 d-flex align-items-center justify-content-center bg-gradient">
          <div className="text-center">
            <div className="loading-spinner mb-4">
              <Spinner animation="border" variant="primary" size="lg" />
            </div>
            <h4 className="text-white mb-2">Vérification en cours</h4>
            <p className="text-white-50">Veuillez patienter...</p>
          </div>
        </Container>
      </div>
    );
  }
  
  // Token invalide
  if (!tokenValid) {
    return (
      <div className="reset-password-page">
        <Container fluid className="min-vh-100 d-flex align-items-center justify-content-center bg-gradient">
          <Row className="justify-content-center w-100">
            <Col md={6} lg={5} xl={4}>
              <Card className="border-0 shadow-xl">
                <Card.Body className="p-4 p-lg-5 text-center">
                  <div className="invalid-icon mb-4">
                    <XCircle size={64} className="text-danger" />
                  </div>
                  <h3 className="mb-2">Lien invalide</h3>
                  <p className="text-muted mb-4">
                    {error || "Le lien de réinitialisation est invalide ou a expiré."}
                  </p>
                  <Button 
                    variant="primary" 
                    as={Link} 
                    to="/forgot-password"
                    className="px-4 py-2 rounded-pill"
                  >
                    Nouvelle demande
                  </Button>
                  <div className="mt-3">
                    <Link to="/login" className="text-decoration-none small">
                      <ArrowLeft size={12} className="me-1" />
                      Retour à la connexion
                    </Link>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }
  
  return (
    <div className="reset-password-page">
      <Container fluid className="min-vh-100 d-flex align-items-center justify-content-center bg-gradient">
        <Row className="justify-content-center w-100">
          <Col md={6} lg={5} xl={4}>
            <Card className="border-0 shadow-xl">
              <Card.Body className="p-4 p-lg-5">
                {/* En-tête */}
                <div className="text-center mb-4">
                  <div className="icon-circle mx-auto mb-3 bg-primary-soft">
                    <Key size={28} className="text-primary" />
                  </div>
                  <h2 className="h3 mb-2 fw-bold">Nouveau mot de passe</h2>
                  <p className="text-muted small">
                    Choisissez un mot de passe sécurisé
                  </p>
                </div>
                
                {/* Message de succès */}
                {success && (
                  <Alert variant="success" className="mb-4 border-0 rounded-3">
                    <div className="d-flex align-items-center">
                      <CheckCircle className="me-2 flex-shrink-0" size={20} />
                      <div>
                        <strong>Mot de passe modifié !</strong>
                        <br />
                        <small>Redirection vers la connexion...</small>
                      </div>
                    </div>
                  </Alert>
                )}
                
                {/* Formulaire */}
                {!success && (
                  <Form onSubmit={handleSubmit}>
                    {/* Nouveau mot de passe */}
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold small text-uppercase text-muted">
                        Nouveau mot de passe
                      </Form.Label>
                      <InputGroup>
                        <InputGroup.Text className="bg-light border-end-0">
                          <Lock size={16} className="text-muted" />
                        </InputGroup.Text>
                        <Form.Control
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Entrez votre nouveau mot de passe"
                          className="py-2 border-start-0"
                          disabled={loading}
                        />
                        <Button
                          variant="outline-secondary"
                          onClick={() => setShowPassword(!showPassword)}
                          className="border-start-0"
                        >
                          {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                        </Button>
                      </InputGroup>
                    </Form.Group>
                    
                    {/* Indicateur de force */}
                    {password.length > 0 && (
                      <div className="mb-4">
                        <div className="d-flex justify-content-between mb-1">
                          <small className="text-muted">Force du mot de passe</small>
                          <small className={`text-${getPasswordStrengthScore().color} fw-semibold`}>
                            {getPasswordStrengthScore().text}
                          </small>
                        </div>
                        <div className="progress-custom" style={{ height: "6px" }}>
                          <div 
                            className={`progress-bar-custom bg-${getPasswordStrengthScore().color}`}
                            style={{ width: `${getPasswordStrengthScore().progress}%` }}
                          />
                        </div>
                        <div className="row mt-2 g-1">
                          <div className="col-6">
                            <small className={`${passwordStrength.length ? "text-success" : "text-muted"}`}>
                              {passwordStrength.length ? "✓" : "○"} 8 caractères
                            </small>
                          </div>
                          <div className="col-6">
                            <small className={`${passwordStrength.uppercase ? "text-success" : "text-muted"}`}>
                              {passwordStrength.uppercase ? "✓" : "○"} Majuscule
                            </small>
                          </div>
                          <div className="col-6">
                            <small className={`${passwordStrength.lowercase ? "text-success" : "text-muted"}`}>
                              {passwordStrength.lowercase ? "✓" : "○"} Minuscule
                            </small>
                          </div>
                          <div className="col-6">
                            <small className={`${passwordStrength.number ? "text-success" : "text-muted"}`}>
                              {passwordStrength.number ? "✓" : "○"} Chiffre
                            </small>
                          </div>
                          <div className="col-12">
                            <small className={`${passwordStrength.special ? "text-success" : "text-muted"}`}>
                              {passwordStrength.special ? "✓" : "○"} Caractère spécial
                            </small>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Confirmation */}
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-semibold small text-uppercase text-muted">
                        Confirmer le mot de passe
                      </Form.Label>
                      <InputGroup>
                        <InputGroup.Text className="bg-light border-end-0">
                          <Lock size={16} className="text-muted" />
                        </InputGroup.Text>
                        <Form.Control
                          type={showConfirmPassword ? "text" : "password"}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Confirmez votre mot de passe"
                          className="py-2 border-start-0"
                          disabled={loading}
                          isInvalid={confirmPassword.length > 0 && password !== confirmPassword}
                        />
                        <Button
                          variant="outline-secondary"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="border-start-0"
                        >
                          {showConfirmPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                        </Button>
                      </InputGroup>
                      {confirmPassword.length > 0 && password !== confirmPassword && (
                        <Form.Text className="text-danger">
                          Les mots de passe ne correspondent pas
                        </Form.Text>
                      )}
                    </Form.Group>
                    
                    {/* Erreur */}
                    {error && (
                      <Alert variant="danger" className="mb-3 py-2 rounded-3">
                        <small>{error}</small>
                      </Alert>
                    )}
                    
                    {/* Bouton */}
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={loading || !password || password !== confirmPassword}
                      className="w-100 py-2 rounded-pill mb-3 fw-semibold"
                    >
                      {loading ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" className="me-2" />
                          Réinitialisation...
                        </>
                      ) : (
                        "Réinitialiser le mot de passe"
                      )}
                    </Button>
                    
                    {/* Lien retour */}
                    <div className="text-center">
                      <Link to="/login" className="text-decoration-none small text-muted">
                        <ArrowLeft size={12} className="me-1" />
                        Retour à la connexion
                      </Link>
                    </div>
                  </Form>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
      
      <style>{`
        .reset-password-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }
        
        .bg-gradient {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }
        
        .shadow-xl {
          box-shadow: 0 20px 35px -10px rgba(0, 0, 0, 0.2);
        }
        
        .icon-circle {
          width: 70px;
          height: 70px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .bg-primary-soft {
          background: linear-gradient(135deg, rgba(13, 110, 253, 0.1) 0%, rgba(13, 110, 253, 0.05) 100%);
        }
        
        .invalid-icon {
          width: 80px;
          height: 80px;
          background: rgba(220, 53, 69, 0.1);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto;
        }
        
        .progress-custom {
          background-color: #e9ecef;
          border-radius: 10px;
          overflow: hidden;
        }
        
        .progress-bar-custom {
          transition: width 0.3s ease;
          border-radius: 10px;
        }
        
        .bg-success {
          background-color: #28a745 !important;
        }
        
        .bg-warning {
          background-color: #ffc107 !important;
        }
        
        .bg-info {
          background-color: #17a2b8 !important;
        }
        
        .bg-danger {
          background-color: #dc3545 !important;
        }
        
        .loading-spinner {
          width: 60px;
          height: 60px;
          margin: 0 auto;
        }
        
        .text-white-50 {
          color: rgba(255, 255, 255, 0.7);
        }
        
        .card {
          border-radius: 20px;
          overflow: hidden;
        }
        
        .form-control:focus, .input-group-text:focus {
          box-shadow: none;
          border-color: #dee2e6;
        }
        
        .form-control:focus {
          border-color: #0d6efd;
        }
      `}</style>
    </div>
  );
};

export default ResetPasswordPage;